/**
 * ProofPreviewPage.js — /proof
 *
 * Internal preview surface for the operator to inspect the 4
 * NDOR-generated artifacts (Source CV, Job Description, Tailored CV,
 * Cover Letter) before the homepage Proof Section is wired up.
 *
 * Static content is read from /public/proof/manifest.json (generated
 * by /app/scripts/proof_artifacts/run_ndor.py). No backend round-trip.
 */
import { useEffect, useState } from 'react';

const FILE_LABELS = {
  job_description: 'Job description',
  source_cv:       'Synthetic Source CV',
  tailored_cv:     'NDOR-tailored CV',
  cover_letter:    'NDOR-tailored cover letter',
};

const FILE_BLURBS = {
  job_description: 'Paraphrased Senior Manager — Digital Product Management (Integrated Risk Management) JD. No employer name; no verbatim copy.',
  source_cv:       'Synthetic candidate CV in the same domain (strategy / risk / decision-support). Fake name, fake employers, altered timelines.',
  tailored_cv:     'CV produced by the live NDOR pipeline. Same prompt path your users hit in production.',
  cover_letter:    'Cover letter produced by the live NDOR pipeline. Same prompt path your users hit.',
};

export default function ProofPreviewPage() {
  const [manifest, setManifest] = useState(null);
  const [error, setError]       = useState(null);

  useEffect(() => {
    fetch('/proof/manifest.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(r.statusText))))
      .then(setManifest)
      .catch((e) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-ndor-surface text-ndor-ink p-8">
        <p className="text-sm text-ndor-warning" data-testid="proof-error">
          Could not load /proof/manifest.json — {error}
        </p>
      </div>
    );
  }
  if (!manifest) {
    return (
      <div className="min-h-screen bg-ndor-surface text-ndor-ink p-8">
        <p className="text-sm text-ndor-ink-muted">Loading proof artifacts…</p>
      </div>
    );
  }

  const delta = manifest.verdict_delta;
  const sign = delta > 0 ? '+' : '';

  return (
    <div className="min-h-screen bg-ndor-surface text-ndor-ink py-12 px-6">
      <div className="max-w-4xl mx-auto space-y-8" data-testid="proof-preview-page">
        <header className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.24em] text-ndor-ink-muted">
            Internal · operator preview
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif-display text-ndor-ink">
            NDOR proof — one example outcome
          </h1>
          <p className="text-sm text-ndor-ink-secondary leading-relaxed">
            Synthetic candidate · paraphrased financial-services Integrated Risk Management role · same NDOR pipeline your users hit in production.{' '}
            <span className="italic text-ndor-ink-muted">Results vary.</span>
          </p>
        </header>

        <section
          className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/30 p-6 grid grid-cols-1 sm:grid-cols-3 gap-4"
          data-testid="proof-headline-numbers"
        >
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Source CV verdict</p>
            <p className="text-4xl font-serif-display text-ndor-ink">
              {manifest.source_verdict}<span className="text-base text-ndor-ink-muted">/100</span>
            </p>
            <p className="text-[11px] text-ndor-ink-muted">LLM-graded NDOR CV verdict before tailoring.</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Tailored CV verdict</p>
            <p className="text-4xl font-serif-display text-ndor-ink">
              {manifest.tailored_verdict}<span className="text-base text-ndor-ink-muted">/100</span>
            </p>
            <p className="text-[11px] text-ndor-ink-muted">Same JD; same grader; CV tailored by NDOR.</p>
          </div>
          <div className="space-y-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-ndor-ink-muted">Lift</p>
            <p className={'text-4xl font-serif-display ' + (delta > 0 ? 'text-ndor-success' : 'text-ndor-ink')}>
              {sign}{delta}
            </p>
            <p className="text-[11px] text-ndor-ink-muted">NDOR CV verdict points, JD held constant.</p>
          </div>
        </section>

        <section className="space-y-3" data-testid="proof-downloads">
          <h2 className="text-base text-ndor-ink">Download artifacts</h2>
          <ul className="space-y-2">
            {Object.entries(manifest.files).map(([key, href]) => (
              <li
                key={key}
                className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/20 p-4 flex items-start justify-between gap-4"
                data-testid={`proof-row-${key}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ndor-ink">{FILE_LABELS[key] || key}</p>
                  <p className="text-[11px] text-ndor-ink-muted leading-relaxed mt-0.5">
                    {FILE_BLURBS[key]}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-ndor-gold underline whitespace-nowrap"
                    data-testid={`proof-view-${key}`}
                  >
                    View
                  </a>
                  <a
                    href={href}
                    download
                    className="text-xs text-ndor-gold underline whitespace-nowrap"
                    data-testid={`proof-download-${key}`}
                  >
                    Download
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-2" data-testid="proof-pipeline-meta">
          <h2 className="text-base text-ndor-ink">Pipeline trace</h2>
          <ul className="text-[11px] text-ndor-ink-muted leading-relaxed">
            <li>Grader model: {manifest.models?.grader || '—'}</li>
            <li>CV variant model: {manifest.models?.cv_variant || '—'}</li>
            <li>Cover letter model: {manifest.models?.cover_letter || '—'}</li>
          </ul>
          <p className="text-[11px] text-ndor-ink-muted leading-relaxed">
            Fabrication checks (CV / cover letter) ran inside `ndor.router.generate_cv` and `generate_cover_letter`.
            Manifest with full meta:{' '}
            <a href="/proof/manifest.json" target="_blank" rel="noopener noreferrer" className="text-ndor-gold underline">
              /proof/manifest.json
            </a>.
          </p>
        </section>
      </div>
    </div>
  );
}
